/**
 * Labels and emissions for the intelligent-import factor review screen.
 *
 * The catalogue rate and its denominator stay as the API sent them
 * (`co2e_per_unit` + `unit_type`, or `emissions.factor_value` + `emissions.factor_unit`).
 * Calculated kgCO₂e comes from `emissions.kgco2e`. This module does not convert units.
 * If the reviewer edits the quantity, the new total applies the ratio the server already
 * computed (`amount_in_factor_units / activity_amount`) to the catalogue rate.
 */

const UNIT_FAMILY_LABELS = {
  spend: '£',
  energy: 'kWh',
  volume: 'litres',
  distance: 'km',
  area: 'm²',
};

/** Spelling variants of the same unit. Not a conversion table — kg and tonne stay distinct. */
const IDENTITY_ALIASES = {
  litre: 'litres',
  liter: 'litres',
  liters: 'litres',
  l: 'litres',
  kgs: 'kg',
  kilogram: 'kg',
  kilograms: 'kg',
  units: 'unit',
};

const isIdLike = (value) => {
  const s = String(value || '').trim();
  if (!s) return true;
  if (/^\d+$/.test(s)) return true;
  return /^[a-f0-9-]{20,}$/i.test(s);
};

/**
 * Lowercase token for an equality check. `tonne` and `tonnes` match; `tCO₂e` is not a tonne.
 * @param {string|null|undefined} unit
 * @returns {string}
 */
export const canonicalUnitToken = (unit) => {
  if (unit == null) return '';
  const compact = String(unit).trim().toLowerCase().replace(/\s+/g, '').replace('₂', '2');
  if (!compact) return '';
  if (compact === 'tonne') return 'tonnes';
  return IDENTITY_ALIASES[compact] || compact;
};

/**
 * @param {string|null|undefined} a
 * @param {string|null|undefined} b
 * @returns {boolean}
 */
export const unitsAreEquivalent = (a, b) => {
  const left = canonicalUnitToken(a);
  const right = canonicalUnitToken(b);
  return Boolean(left && right && left === right);
};

const humanUnit = (value, metricsById = {}) => {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (!isIdLike(raw)) return raw;
  const named = metricsById[raw];
  if (named && !isIdLike(named)) return String(named).trim();
  return null;
};

const unitFromFactorName = (match) => {
  const name = match?.factorName || match?.factor_name || match?.factor_display_name || '';
  const parts = String(name).split(' · ');
  if (parts.length < 2) return null;
  const last = parts[parts.length - 1].trim();
  if (!last || last.length >= 30 || /\bspend\b/i.test(last)) return null;
  return last;
};

/**
 * Denominator of the emission factor, from factor metadata only.
 * A mass `unit_family` is not used: that label is always `kg` and would show a per-tonne factor as kgCO₂e/kg.
 * @param {object|null|undefined} match
 * @param {Record<string, string>} [metricsById]
 * @returns {string|null}
 */
export const factorRateUnit = (match, metricsById = {}) => {
  if (!match) return null;
  const emissions = match.emissions;
  const fromEmissions = humanUnit(emissions?.factorUnit || emissions?.factor_unit, metricsById);
  if (fromEmissions) return fromEmissions;

  const fromType = humanUnit(match.unitType || match.unit_type, metricsById);
  if (fromType) return fromType;

  const fromMetric = humanUnit(match.metric || match.unit, metricsById);
  if (fromMetric) return fromMetric;

  const dims = match.factorDimensions || match.factor_dimensions || {};
  const fromDims = humanUnit(dims.unit, metricsById);
  if (fromDims) return fromDims;

  const fromName = unitFromFactorName(match);
  if (fromName) return fromName;

  const family = match.unitFamily || match.unit_family || dims.unit_family || dims.unitFamily;
  if (family && family !== 'mass' && UNIT_FAMILY_LABELS[family]) return UNIT_FAMILY_LABELS[family];
  return null;
};

/**
 * Unit of the quantity in the activity box. Pre-filled amounts stay in the detected unit.
 * @param {object|null|undefined} match
 * @param {object|null|undefined} row
 * @param {Record<string, string>} [metricsById]
 * @param {(unit: string) => boolean} [isCurrencyUnit]
 * @returns {string|null}
 */
export const activityQuantityUnit = (match, row, metricsById = {}, isCurrencyUnit = () => false) => {
  const detected = match?.detectedUnit || match?.detected_unit || row?.detectedUnit || row?.detected_unit;
  if (detected != null && String(detected).trim() !== '' && !isCurrencyUnit(detected)) {
    return String(detected).trim();
  }
  return factorRateUnit(match, metricsById);
};

const parseFinite = (value) => {
  if (value == null || value === '') return null;
  const n = parseFloat(value);
  return Number.isFinite(n) ? n : null;
};

/**
 * Catalogue kgCO₂e per factor unit. Prefers the unscaled factor value when the payload has one.
 * @param {object|null|undefined} match
 * @returns {number|null}
 */
export const catalogueFactorValue = (match) => {
  if (!match) return null;
  const emissions = match.emissions;
  const explicit = parseFinite(
    emissions?.factorValue ?? emissions?.factor_value ?? match.co2ePerFactorUnit ?? match.co2e_per_factor_unit,
  );
  if (explicit != null) return explicit;
  return parseFinite(match.co2ePerUnit ?? match.co2e_per_unit);
};

const fmt2dp = (value) => {
  const n = parseFloat(value);
  return Number.isNaN(n) ? String(value) : n.toFixed(2);
};

/**
 * Factor line, e.g. `801.52 kgCO₂e/tonne`. Empty when the value or its own unit is missing.
 * @param {object|null|undefined} match
 * @param {Record<string, string>} [metricsById]
 * @returns {string}
 */
export const formatFactorRate = (match, metricsById = {}) => {
  const value = catalogueFactorValue(match);
  const unit = factorRateUnit(match, metricsById);
  if (value == null || value === 0 || !unit) return '';
  return `${fmt2dp(value)} kgCO₂e/${unit}`;
};

/**
 * Display string for a kgCO₂e figure. Keeps the server's 2 d.p. and drops trailing zeros.
 * @param {number|null|undefined} kgco2e
 * @returns {string|null}
 */
export const formatKgCo2e = (kgco2e) => {
  if (kgco2e == null || !Number.isFinite(Number(kgco2e))) return null;
  const rounded = Math.round(Number(kgco2e) * 100) / 100;
  return String(rounded);
};

const backendQuantityRatio = (emissions, unitConversion) => {
  const base = parseFinite(emissions?.activityAmount ?? emissions?.activity_amount);
  const inFactor = parseFinite(emissions?.amountInFactorUnits ?? emissions?.amount_in_factor_units);
  if (base != null && base !== 0 && inFactor != null) return inFactor / base;
  const factor = parseFinite(unitConversion?.factor);
  if (factor != null) return factor;
  if (emissions?.reason === 'same') return 1;
  return null;
};

/**
 * kgCO₂e to show next to an activity quantity.
 * Uses the server total when the quantity is the one the server calculated.
 * An edited quantity is scaled by that server ratio. Without an `emissions` object,
 * the screen only multiplies when the activity unit and the factor unit are the same.
 *
 * @param {number|string|null|undefined} amount
 * @param {object|null|undefined} match
 * @param {{ row?: object, metricsById?: Record<string, string>, activityUnit?: string|null, quantityInFactorUnit?: boolean, isCurrencyUnit?: (unit: string) => boolean }} [options]
 * @returns {{ kgco2e: number|null, text: string|null, status: string }}
 */
export const describeActivityEmissions = (amount, match, options = {}) => {
  const missing = { kgco2e: null, text: null, status: 'missing' };
  const entered = parseFinite(amount);
  if (entered == null) return missing;

  const rate = catalogueFactorValue(match);
  if (rate == null) return missing;

  if (options.quantityInFactorUnit) {
    const kgco2e = Math.round(Math.abs(entered) * rate * 10) / 10;
    return { kgco2e, text: kgco2e.toFixed(1), status: 'direct' };
  }

  const emissions = match?.emissions;
  if (emissions && typeof emissions === 'object') {
    if (emissions.compatible === false) {
      const status = emissions.reason === 'missing_unit' || emissions.reason === 'missing_value'
        ? 'missing'
        : 'incompatible';
      return { kgco2e: null, text: null, status };
    }
    const base = parseFinite(emissions.activityAmount ?? emissions.activity_amount);
    const serverKg = parseFinite(emissions.kgco2e);
    if (base != null && serverKg != null && Math.abs(Math.abs(entered) - Math.abs(base)) < 1e-6) {
      return { kgco2e: serverKg, text: formatKgCo2e(serverKg), status: 'backend' };
    }
    const ratio = backendQuantityRatio(emissions, match.unitConversion || match.unit_conversion);
    const factorValue = parseFinite(emissions.factorValue ?? emissions.factor_value) ?? rate;
    if (ratio == null) return { kgco2e: null, text: null, status: 'missing' };
    const kgco2e = Math.round(Math.abs(entered) * ratio * factorValue * 100) / 100;
    return { kgco2e, text: formatKgCo2e(kgco2e), status: 'scaled' };
  }

  const activityUnit = options.activityUnit
    || activityQuantityUnit(match, options.row, options.metricsById, options.isCurrencyUnit);
  const factorUnit = factorRateUnit(match, options.metricsById);
  if (activityUnit && factorUnit && !unitsAreEquivalent(activityUnit, factorUnit)) {
    // Different units and no server total. Do not multiply, and do not guess a conversion.
    return { kgco2e: null, text: null, status: 'unavailable' };
  }
  if ((activityUnit && !factorUnit) || (!activityUnit && factorUnit)) {
    return { kgco2e: null, text: null, status: 'missing' };
  }

  const kgco2e = Math.round(Math.abs(entered) * rate * 10) / 10;
  return { kgco2e, text: kgco2e.toFixed(1), status: 'direct' };
};

/**
 * Suggestion fields the review screen needs. Explicit so a parent match's top-factor
 * `emissions` is not reused for a different suggestion.
 * @param {object|null|undefined} suggestion - camelCased suggestion
 * @returns {{ unitType: string|null, unit_type: string|null, emissions: object|null, emissionsKgco2e: number|null, unitConversion: object|null, co2ePerFactorUnit: number|null }}
 */
export const pickSuggestionEmissionFields = (suggestion) => {
  const s = suggestion || {};
  const emissions = s.emissions && typeof s.emissions === 'object' ? s.emissions : null;
  const unitType = s.unitType || s.unit_type || null;
  const unitConversion = s.unitConversion || s.unit_conversion || null;
  const co2ePerFactorUnit = parseFinite(s.co2ePerFactorUnit ?? s.co2e_per_factor_unit);
  const emissionsKgco2e = parseFinite(s.emissionsKgco2e ?? s.emissions_kgco2e ?? emissions?.kgco2e);
  return {
    unitType,
    unit_type: unitType,
    emissions,
    emissionsKgco2e,
    unitConversion,
    co2ePerFactorUnit,
  };
};
